/**
 * BottomSheet — reusable mobile sheet.
 *
 * The mobile analogue of the desktop centered modal: slides up from the bottom,
 * rounded top corners, drag handle, blurred backdrop, swipe-down or tap-backdrop
 * to dismiss. Used by the drawer, quick-capture, and any mobile modal surface.
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Tailwind max-height for the panel; defaults to 90vh. */
  maxHeightClass?: string;
}

export function BottomSheet({ open, onClose, title, children, maxHeightClass = 'max-h-[90vh]' }: BottomSheetProps) {
  const [mounted, setMounted] = useState(open);
  const startY = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const onTouchStart = (e: React.TouchEvent) => { startY.current = e.touches[0].clientY; };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0) setDragY(delta);
  };
  const onTouchEnd = () => {
    if (dragY > 110) onClose();
    setDragY(0);
    startY.current = null;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" role="dialog" aria-modal="true">
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${open ? 'pa-anim-fade' : 'opacity-0'} transition-opacity duration-200`}
        onClick={onClose}
      />
      <div
        className={`relative w-full ${maxHeightClass} flex flex-col bg-white dark:bg-[var(--surface)] rounded-t-2xl border-t border-x border-gray-200 dark:border-[var(--rule)] shadow-2xl ${open ? 'pa-anim-sheet' : ''}`}
        style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pt-3 pb-2 flex-shrink-0 cursor-grab active:cursor-grabbing"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="mx-auto h-1.5 w-10 rounded-full bg-gray-300 dark:bg-[var(--rule-bright)]" />
        </div>
        {title && (
          <div className="px-5 pb-3 flex-shrink-0 flex items-center justify-between gap-3">
            <h2 className="font-fieldwork text-lg text-gray-900 dark:text-[var(--text-pri)]">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="h-9 w-9 flex-shrink-0 rounded-full flex items-center justify-center text-gray-500 dark:text-[var(--text-ter)] hover:bg-gray-100 dark:hover:bg-[var(--hover-tint)] active:bg-gray-200 dark:active:bg-[var(--active-tint)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="pa-scroll overflow-y-auto px-5 pb-[calc(1.25rem+var(--pa-safe-bottom))]">
          {children}
        </div>
      </div>
    </div>
  );
}
